import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import OutlinedInput from '@mui/material/OutlinedInput'
import Button from '@mui/material/Button'
import { useNavigate } from 'react-router-dom'
import brushfireLogo from '@/features/auth/assets/login/brushfire-logo.png'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useSessionStore } from '@/stores/useSessionStore'
import { authInvalidEmailBorder } from '@/theme/designTokens'
import {
  FORGOT_PASSWORD_EMAIL_STATE_KEY,
  NO_PASSWORD_SETUP_STATE_KEY,
} from '../lib/constants'
import {
  setPasswordContinueSchema,
  type SetPasswordContinueFormData,
} from '../lib/reset-password-schema'
import { errorInputSx } from '../styles/authFormStyles'
import {
  authSplitEmailLabelSx,
  authSplitFormBodySx,
  authSplitFormHeaderSx,
  authSplitFormLogoSx,
  authSplitFormPageSx,
  loginFieldErrorSx,
  loginInputNativeProps,
  loginInputSx,
  loginPrimaryButtonSx,
  loginWelcomeHeadingSx,
  loginWelcomeSubtitleSx,
} from '../styles/loginSplitStyles'

function emailFieldErrorSx(hasError: boolean) {
  if (!hasError) return {}
  return {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: authInvalidEmailBorder,
    },
  }
}

/**
 * Figma 5074-12371 — split right panel after signup session TTL:
 * Welcome header (5074-12356/12359/12360), editable Email (5074-12364/12367),
 * Continue Login (5074-12370) → password-reset flow (forgot-password → verify-otp → reset-password).
 */
export function SetPasswordPage() {
  useDocumentTitle('Set Up Password')
  const navigate = useNavigate()
  const sessionEmail = useSessionStore((s) => s.user?.email ?? '')

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetPasswordContinueFormData>({
    resolver: zodResolver(setPasswordContinueSchema),
    defaultValues: { email: sessionEmail },
  })

  const onSubmit = (data: SetPasswordContinueFormData) => {
    const email = data.email.trim().toLowerCase()
    navigate('/forgot-password', {
      replace: true,
      state: {
        [FORGOT_PASSWORD_EMAIL_STATE_KEY]: email,
        [NO_PASSWORD_SETUP_STATE_KEY]: true,
      },
    })
  }

  return (
    <Box data-testid="set-password-page" sx={authSplitFormPageSx}>
      <Box sx={authSplitFormHeaderSx} data-testid="set-password-header">
        <Box
          component="img"
          src={brushfireLogo}
          alt="Brushfire"
          data-testid="set-password-logo"
          sx={authSplitFormLogoSx}
        />
        <Typography component="h1" sx={loginWelcomeHeadingSx}>
          Welcome
        </Typography>
        <Typography sx={{ ...loginWelcomeSubtitleSx, mt: '16px' }}>
          Enter your registered email address here.
        </Typography>
      </Box>

      <Box sx={authSplitFormBodySx}>
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
        >
          <Box data-testid="set-password-email-field">
            <Typography
              component="label"
              htmlFor="set-password-email"
              sx={authSplitEmailLabelSx}
            >
              Email
            </Typography>
            <OutlinedInput
              id="set-password-email"
              type="email"
              placeholder="Enter your email"
              fullWidth
              autoComplete="email"
              error={!!errors.email}
              sx={{
                ...loginInputSx,
                ...(errors.email ? { ...errorInputSx, ...emailFieldErrorSx(true) } : {}),
              }}
              inputProps={loginInputNativeProps}
              {...register('email')}
            />
            {errors.email && (
              <Typography sx={loginFieldErrorSx}>{errors.email.message}</Typography>
            )}
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disableElevation
            data-testid="set-password-submit"
            sx={loginPrimaryButtonSx}
          >
            Continue Login
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
